import React, { useState, useEffect, useRef } from "react";
import { Databases } from "appwrite";
import client from "./appwriteConfig";
import "./css/BeatMaker.css";
import InstrumentAudioContext from "./InstrumentAudioContext";
import KitModal from "./KitModal";
import { FaPlay, FaPause, FaCircle, FaClock } from "react-icons/fa";
import Sequencer from "./BeatSeq.js";
import { BeatLoader, ClipLoader } from "react-spinners";

import { PatternInsSeqProvider } from "./PatternInsSeqContext.js";
import SongPlayer1 from "./SongPlayer1";
import SongPlayer from "./SongPlayer";
import { useUser } from "./UserContext";
import Plans from "./Plans";
import { useNavigate } from "react-router-dom";
import Vocals from "./VocalsSeq.js";
import BeatSeq from "./BeatSeq.js";
import VocalsSeq from "./VocalsSeq.js";
function BeatMaker() {
  const navigate = useNavigate();
  const [instrumentData, setInstrumentData] = useState([]);
  const [pads, setPads] = useState([]);
  const [editSongPatternNumber, setEditSongPatternNumber] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [sequence, setSequence] = useState(
    Array(pads.length).fill(Array(8).fill(false)),
  );
  const [isGlobalPlaying, setIsGlobalPlaying] = useState(false);
  const [transformedData, setTransformedData] = useState(null);
  const [selectedKit, setSelectedKit] = useState(null);
  const [bpm, setBpm] = useState(InstrumentAudioContext.getBpm());
  const [isLedPlaying, setIsLedPlaying] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [recordedSteps, setRecordedSteps] = useState([]);
  const activeTouchesRef = useRef(new Set());
  const { user, userPlan } = useUser();

  const isTrial = !userPlan || userPlan === "trial";
  const [trialSeconds, setTrialSeconds] = useState(0);
  const [showPlansModal, setShowPlansModal] = useState(false);
  const [stepCount, setStepCount] = useState(16);
  const [overwriteMode, setOverwriteMode] = useState(false);
  const now = new Date();

  // e.g. 2026-07-05-14
  const hourKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}-${String(now.getHours()).padStart(2, "0")}`;
  const trialKey = `beatmakerPresetTrial_${user?.$id || "guest"}_${hourKey}`;

  const trialDropdownLocked = isTrial && trialSeconds >= 40 * 60;
  useEffect(() => {
    fetchPads();
  }, []);
  useEffect(() => {
    if (!isTrial) {
      setTrialSeconds(0);
      setShowPlansModal(false);
      return;
    }

    const savedSeconds = Math.min(
      Number(localStorage.getItem(trialKey) || 0),
      300,
    );

    setTrialSeconds(savedSeconds);

    const timer = setInterval(() => {
      setTrialSeconds((prev) => {
        if (prev >= 300) {
          clearInterval(timer);
          return 300;
        }

        const next = Math.min(prev + 1, 300);
        localStorage.setItem(trialKey, String(next));
        return next;
      });
    }, 1000);

    const firstModal = setTimeout(
      () => {
        setShowPlansModal(true);
      },
      2 * 60 * 1000,
    );

    const repeatModal = setInterval(
      () => {
        setShowPlansModal(true);
      },
      3 * 60 * 1000,
    );

    return () => {
      clearInterval(timer);
      clearTimeout(firstModal);
      clearInterval(repeatModal);
    };
  }, [isTrial, trialKey]);
  useEffect(() => {
    pads.forEach((pad) => {
      InstrumentAudioContext.preloadAudio(pad.kitUrl);
    });
  }, [pads]);
  useEffect(() => {
    InstrumentAudioContext.setBpm(bpm);
  }, [bpm]);

  useEffect(() => {
    if (!isRecording && recordedSteps.length > 0) {
      const transformedData = transformRecordedSteps(recordedSteps);
      console.log("Transformed Recorded Notes:", transformedData);
    }
  }, [isRecording, recordedSteps]);

  const fetchPads = async () => {
    setLoading(true);
    try {
      const databases = new Databases(client);
      const { documents } = await databases.listDocuments(
        process.env.REACT_APP_DATABASE_ID,
        process.env.REACT_APP_COLLECTION_KIT_ID,
      );
      const typedPads = documents.map((pad) => ({
        ...pad,
        type: pad.kitId,
      }));
      setPads(typedPads);
      setLoading(false);
    } catch (error) {
      console.error("Failed to fetch pads:", error);
      setError("Failed to load pads.");
      setLoading(false);
    }
  };
  const handlePadClick = (kitUrl, kitName) => {
    InstrumentAudioContext.playSound(kitUrl);

    if (!isRecording) return;

    if (!isLedPlaying && !isGlobalPlaying) {
      startLedSequence();
    }

    const stepData = { kitName, step: currentStep };
    setRecordedSteps((prevSteps) => [...prevSteps, stepData]);
  };
  const handleMouseDown = (kitUrl, kitName) => {
    handlePadClick(kitUrl, kitName);
  };

  const handleTouchStart = (e, kitUrl, kitName) => {
    e.preventDefault();
    Array.from(e.changedTouches).forEach((touch) => {
      const touchId = touch.identifier;
      if (!activeTouchesRef.current.has(touchId)) {
        activeTouchesRef.current.add(touchId);
        handlePadClick(kitUrl, kitName);
      }
    });
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    Array.from(e.changedTouches).forEach((touch) => {
      const touchId = touch.identifier;
      activeTouchesRef.current.delete(touchId);
    });
  };

  const transformRecordedSteps = (recordedSteps) => {
    const result = {};

    recordedSteps.forEach(({ kitName, step }) => {
      const lowerCaseKitName = kitName.toLowerCase();
      if (!result[lowerCaseKitName]) {
        result[lowerCaseKitName] = {
          sequence: Array(stepCount).fill(0),
          kitVolume: 0.5,
        };
      }
      result[lowerCaseKitName].sequence[step] = 1;
    });

    return result;
  };

  const startLedSequence = () => {
    const interval = (60 / bpm) * 250;
    setRecordedSteps([]); // Clear recorded steps at the start of each recording session
    setCurrentStep(0);

    const intervalId = setInterval(() => {
      setCurrentStep((prevStep) => {
        const nextStep = (prevStep + 1) % stepCount;
        if (nextStep === 0 && isRecording) {
          setIsRecording(false);
        }
        if (nextStep === 0) {
          clearInterval(intervalId);
          setIsLedPlaying(false);
        }
        return nextStep;
      });
    }, interval);

    setIsLedPlaying(true);
  };

  const handleMapButtonClick = () => {
    const data = transformRecordedSteps(recordedSteps);

    setTransformedData({
      patterns: data,
      overwriteMode,
    });

    console.log("Transformed Data:", data);
  };

  const handleClockButtonClick = () => {
    InstrumentAudioContext.toggleMetronome();
  };

  return (
    <div className="beat-maker">
      <h2>Beat Maker</h2>{" "}
      {loading ? (
        <div style={{ textAlign: "center" }}>
          <BeatLoader color="#3498db" />
        </div>
      ) : (
        <>
          {" "}
          <div className="pads">
            {pads.map((pad) => (
              <button
                key={pad.$id}
                className="pad"
                data-kitid={pad.kitUrl}
                onMouseDown={() => handleMouseDown(pad.kitUrl, pad.kitName)}
                onTouchStart={(e) =>
                  handleTouchStart(e, pad.kitUrl, pad.kitName)
                }
                onTouchEnd={handleTouchEnd}
              >
                {pad.kitName || "Unnamed Pad"}
              </button>
            ))}{" "}
          </div>
          <div className="led-indicator">
            {" "}
            {Array(stepCount)
              .fill()
              .map((_, index) => (
                <div
                  key={index}
                  className={`led-step ${
                    currentStep === index ? "active" : ""
                  } ${index % 4 === 0 ? "special-step" : ""}`}
                />
              ))}{" "}
          </div>{" "}
          {isTrial && (
            <div
              className="trial-timer-pill"
              onClick={() => navigate("/plans")}
              title="View plans"
            >
              Preset Trial: {Math.floor(trialSeconds / 60)}:
              {String(trialSeconds % 60).padStart(2, "0")} / 5:00
              <span> Genre presets lock after 5 mins daily in TRIAL mode</span>
            </div>
          )}
          {showModal && (
            <KitModal
              onClose={() => setShowModal(false)}
              refreshPads={fetchPads}
            />
          )}
          <PatternInsSeqProvider>
            <BeatSeq
              pads={pads}
              sequence={sequence}
              setSequence={setSequence}
              isPlaying={isPlaying}
              bpm={bpm}
              setBpm={setBpm}
              transformedData={transformedData}
              updateInstruments={(instruments) =>
                setInstrumentData(instruments)
              }
              editSongPatternNumber={editSongPatternNumber}
              setEditSongPatternNumber={setEditSongPatternNumber}
              onAddKit={() => setShowModal(true)}
              isRecording={isRecording}
              setIsRecording={setIsRecording}
              handleMapButtonClick={handleMapButtonClick}
              handleClockButtonClick={handleClockButtonClick}
              trialDropdownLocked={trialDropdownLocked}
              openPlansModal={() => setShowPlansModal(true)}
              trialSeconds={trialSeconds}
              isTrial={isTrial}
              stepCount={stepCount}
              setStepCount={setStepCount}
              onStepChange={setCurrentStep}
              onGlobalPlayChange={setIsGlobalPlaying}
              overwriteMode={overwriteMode}
              setOverwriteMode={setOverwriteMode}
            />
            <VocalsSeq bpm={bpm} stepCount={stepCount} />
            {/* <SongPlayer bpm={bpm} setBpm={setBpm} pads={pads} /> */}
            <SongPlayer
              bpm={bpm}
              setBpm={setBpm}
              pads={pads}
              onEditPatternFromSong={setEditSongPatternNumber}
            />
          </PatternInsSeqProvider>
        </>
      )}{" "}
      {showPlansModal && isTrial && (
        <div className="plans-modal-overlay">
          <div className="plans-modal-content">
            <button
              className="close-button-melody"
              onClick={() => setShowPlansModal(false)}
            >
              ×
            </button>
            <Plans />
          </div>
        </div>
      )}
    </div>
  );
}

export default BeatMaker;
